import React, { FC } from 'react'
import { Paper } from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { DataLogger } from 'src/api'
import { toInfoColumn } from 'src/components/columns/infoColumn'
import { usePatchDataLoggerRequest } from 'src/hooks/api'
import { useAddPopupMessage } from 'src/hooks/ui'
import { DataLoggerGridColumns } from './dataLoggerColumns'

const gridSx = {
  border: 'none',
  '& .MuiDataGrid-columnHeaderTitleContainerContent': { paddingLeft: '5px', overflow: 'visible' },
  '& .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'normal',
    lineHeight: 1,
    fontSize: '13px',
    fontWeight: 400,
    color: 'rgba(0, 0, 0, 0.6)',
    overflow: 'visible'
  }
}

const columns: GridColDef<DataLogger>[] = [
  // The list column self-links to the logger's own page — drop it here.
  toInfoColumn(DataLoggerGridColumns.DeviceId, { renderCell: undefined }),
  toInfoColumn(DataLoggerGridColumns.Label),
  toInfoColumn(DataLoggerGridColumns.Interval),
  // The list renders this as a boolean checkbox; the info grid shows Yes/No/- text.
  toInfoColumn(DataLoggerGridColumns.MatchReadingsToTimeInterval, {
    type: 'string',
    valueGetter: ({ row }) => (typeof row.isMatchReadingsToTimeInterval === 'boolean' ? (row.isMatchReadingsToTimeInterval ? 'Yes' : 'No') : '-')
  }),
  toInfoColumn(DataLoggerGridColumns.Contractor),
  toInfoColumn(DataLoggerGridColumns.Project),
  toInfoColumn(DataLoggerGridColumns.Section, { headerName: 'Project Section' }),
  DataLoggerGridColumns.Comment
]

interface Props {
  dataLogger: DataLogger
  refetchDataLogger?: () => void
}

export const DataLoggerInfo: FC<Props> = ({ dataLogger, refetchDataLogger }) => {
  const addMessage = useAddPopupMessage()
  const updateDataLoggerRequest = usePatchDataLoggerRequest()
  const gridMinWidth = columns.reduce((sum, column) => sum + (column.minWidth ?? 100), 0)

  const processRowUpdate = async (newRow: DataLogger, oldRow: DataLogger) => {
    const { comment: newComment } = newRow
    const { comment: oldComment } = oldRow
    const updatedRow: DataLogger = await new Promise((resolve) => {
      if (newComment !== oldComment) {
        updateDataLoggerRequest.mutate(
          {
            deviceId: newRow.deviceId,
            payload: { comment: newComment ?? null }
          },
          {
            onSuccess: () => {
              addMessage({ text: 'The comment updated', type: 'success' })
              if (typeof refetchDataLogger === 'function') refetchDataLogger()
              resolve(newRow)
            },
            onError: (error) => {
              addMessage({ text: error?.message, type: 'error' })
              resolve(oldRow)
            }
          }
        )
      }
      return newRow
    })
    return updatedRow
  }

  return (
    <Paper sx={{ width: '100%', overflowX: 'auto' }}>
      <DataGridPremium<DataLogger>
        autoHeight
        disableRowSelectionOnClick
        hideFooter
        rows={[dataLogger]}
        columns={columns}
        getRowId={(row) => row.deviceId}
        rowThreshold={2}
        columnThreshold={2}
        localeText={{ columnMenuSortAsc: 'Sort A-Z', columnMenuSortDesc: 'Sort Z-A' }}
        sx={{ ...gridSx, minWidth: gridMinWidth }}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          if (error) addMessage({ text: error, type: 'error' })
        }}
      />
    </Paper>
  )
}
